import React, { useCallback, useState } from 'react';

// Redux
import { useSelector } from 'react-redux';

// Hooks
import { Link } from 'react-router-dom';
import useScreenWidth from '~/hooks/useScreenWidth';
import { displayTooltip } from '~/hooks/displayTooltip';

// Utils
import { dateFormat } from '~/utils/date-format';
import { MESSAGES } from '~/utils/message';
import { DEFAULT_PAGE_SIZES } from '~/utils/constants';
import { downloadFile } from '~/utils/helperFunctions';

// Components

// MUI Components
import DataGrid from '~/components/General/OlcDataGrid';
import {
  Box,
  Pagination,
  PaginationItem,
  Typography,
  Stack,
  MenuItem,
  Select,
  CircularProgress,
  Tooltip,
} from '@mui/material';

// Assets
import { DownloadCIcon, ExportModalCIcon, NoContactCIcon } from '~/assets/images';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

// Styles
import './styles.scss';

const ExportLogsTable = ({ pagination, updatePagination }) => {
  const {
    exportLogs: rows = [],
    to,
    from,
    lastPage = 1,
    currentPage = 1,
    loading: isLoading,
    rowsCount = 0,
  } = useSelector((state) => state.exportLogsReducers);
  const perPage = useSelector((state) => state.exportLogsReducers.perPage) || DEFAULT_PAGE_SIZES[0];
  const isTooltip = displayTooltip(1800);

  const [downloading, setDownloading] = useState({
    status: false,
    logId: null,
  });

  const handleChangePage = useCallback(
    (event, newPage) => {
      updatePagination({ page: newPage, pageSize: pagination.pageSize });
    },
    [pagination.pageSize, updatePagination]
  );

  const handleChangePageSize = useCallback(
    (event) => {
      updatePagination({ page: 0, pageSize: event.target.value });
    },
    [updatePagination]
  );

  const downloadCSV = async (csvFileUrl, csvFileName, logId) => {
    setDownloading({
      status: true,
      logId: logId,
    });

    await downloadFile(csvFileUrl, csvFileName);

    setDownloading({
      status: false,
      logId: null,
    });
  };

  const columns = [
    {
      field: 'exportedBy',
      headerName: 'Exported By',
      width: 250,
      flex: useScreenWidth(1350) ? 1 : 0,
      sortable: false,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{params.row.creator.fullName}</Typography>
        </Box>
      ),
    },
    {
      field: 'exportedDate',
      headerName: 'Exported Date',
      width: 250,
      flex: useScreenWidth(1350) ? 1 : 0,
      sortable: false,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{dateFormat(params.row.createdAt)}</Typography>
        </Box>
      ),
    },
    {
      field: 'exportType',
      headerName: 'Export Type',
      width: 250,
      flex: useScreenWidth(1350) ? 1 : 0,
      sortable: false,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>
            {params.row.module
              ?.replace(/([A-Z])/g, ' $1')
              ?.replace(/^./, (str) => str.toUpperCase())
              ?.trim()}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'exportList',
      headerName: 'Export List',
      width: 300,
      flex: useScreenWidth(1350) ? 1 : 0,
      sortable: false,
      renderCell: (params) => {
        return !isTooltip ? (
          <Tooltip title={params.row.fileName}>
            <Box
              className="addressWrapper linkWrapper"
              sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}
              onClick={() => {
                downloadCSV(params.row.fileUrl, params.row.fileName, params.row.id);
              }}
            >
              {downloading.status && downloading.logId === params?.row?.id ? (
                <CircularProgress
                  sx={{
                    color: '#ed5c2f',
                    width: '20px !important',
                    height: '20px !important',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    svg: {
                      width: '20px',
                      height: '20px',
                    },
                  }}
                >
                  <ExportModalCIcon fill="#000" />
                </Box>
              )}
              <Link className="link">
                <Typography>{params.row.fileName}</Typography>
              </Link>
            </Box>
          </Tooltip>
        ) : (
          <Box
            className="addressWrapper linkWrapper"
            sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}
            onClick={() => {
              downloadCSV(params.row.fileUrl, params.row.fileName, params.row.id);
            }}
          >
            {downloading.status && downloading.logId === params?.row?.id ? (
              <CircularProgress
                sx={{
                  color: '#ed5c2f',
                  width: '20px !important',
                  height: '20px !important',
                }}
              />
            ) : (
              <Box
                sx={{
                  svg: {
                    width: '20px',
                    height: '20px',
                  },
                }}
              >
                <ExportModalCIcon fill="#000" />
              </Box>
            )}
            <Link className="link">
              <Typography>{params.row.fileName}</Typography>
            </Link>
          </Box>
        );
      },
    },
  ];

  return (
    <div className="exportTableWrapper" style={{ marginTop: '25px', width: '100%' }}>
      <DataGrid
        rows={rows}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={DEFAULT_PAGE_SIZES}
        rowCount={rowsCount}
        paginationMode="server"
        hideFooterSelectedRowCount
        hideFooterPagination
        rowSelection={false}
        getRowId={(row) => row?.id}
        disableColumnMenu
        className="exportTableGrid"
        slots={{
          noRowsOverlay: () => (
            <Stack className="noRowsTextContacts">
              <img src={NoContactCIcon} alt="NoContact" />
              {MESSAGES.NO_ROWS_TEXT}
            </Stack>
          ),
        }}
      />
      <Box className="paginationWrapper">
        <Pagination
          count={lastPage}
          variant="outlined"
          shape="rounded"
          onChange={handleChangePage}
          page={currentPage}
          renderItem={(item) => (
            <PaginationItem
              slots={{ previous: ArrowBackIosNewIcon, next: ArrowForwardIosIcon }}
              {...item}
            />
          )}
        />
        <Select
          className={perPage >= 100 ? 'pageSelect pageSelectChange' : 'pageSelect'}
          inputProps={{ 'aria-label': 'Results per page' }}
          value={perPage}
          onChange={handleChangePageSize}
        >
          {DEFAULT_PAGE_SIZES.map((size) => (
            <MenuItem key={size} value={size}>
              {size}
            </MenuItem>
          ))}
        </Select>
        <Typography>
          Showing {rowsCount ? from : 0} to {rowsCount < to ? rowsCount : to} of {rowsCount} results
        </Typography>
      </Box>
    </div>
  );
};

export default ExportLogsTable;
